/**
 * `TABLESTYLE` name resolution for `ACAD_TABLE` entities.
 *
 * An `ACAD_TABLE` names its style by HANDLE (group 342), not by name. The
 * name lives on the `TABLESTYLE` object in the OBJECTS section, which this
 * project retains verbatim in `DrawingDocument.rawObjectGroups` rather than
 * decoding into a typed style table (typed TABLESTYLE records land with the
 * table-editing feature — see `tableEntitySchema`'s boundary doc).
 *
 * This module reads that name straight out of the retained groups. It is a
 * DERIVED read over the one existing source, deliberately NOT a copy stored
 * on the entity: a second stored name could drift from the handle it claims
 * to resolve. And it is a real parse — CLAUDE.md ПРИОРИТЕТ #1 forbids
 * hard-coding `"Standard"` in place of reading the file, which is exactly
 * what a table-style row would otherwise have to do.
 *
 * Source of truth: Autodesk DXF reference, `TABLESTYLE` object
 * (`100 AcDbTableStyle` then group 3 = style name).
 */
import {
  sliceRawObjectRecord,
  type CarriedObjectRecord,
} from "./dxfBridge.carriedObjectRecords";

/** One raw DXF group as retained on the document. */
type RawGroup = { code: number; value: string };

const TABLESTYLE_RECORD_TYPE = "TABLESTYLE";
const TABLESTYLE_SUBCLASS = "AcDbTableStyle";

/**
 * The style NAME for `styleHandle`, or `undefined` when the OBJECTS section
 * carries no matching `TABLESTYLE` record (or one with no name group).
 *
 * `rawObjectGroups` is a FLAT group stream, so records are re-split on the
 * `0` type code exactly the way the DXF itself delimits them. Only a group 3
 * that follows the `100 AcDbTableStyle` subclass marker is accepted: a
 * TABLESTYLE's `102 {ACAD_REACTORS …}` prologue can carry other codes, and a
 * blind "first group 3" read would be free to pick one up from a neighbouring
 * record.
 */
export function resolveTableStyleName(
  rawObjectGroups: readonly RawGroup[] | undefined,
  styleHandle: string | undefined,
): string | undefined {
  if (!rawObjectGroups || !styleHandle) return undefined;
  const wanted = styleHandle.trim().toUpperCase();
  if (wanted === "") return undefined;

  let inRecord = false;
  let handleMatches = false;
  let inSubclass = false;
  for (const group of rawObjectGroups) {
    if (group.code === 0) {
      // A new record starts — commit nothing, just re-arm the scan state.
      inRecord = group.value.trim() === TABLESTYLE_RECORD_TYPE;
      handleMatches = false;
      inSubclass = false;
      continue;
    }
    if (!inRecord) continue;
    if (group.code === 5) {
      handleMatches = group.value.trim().toUpperCase() === wanted;
      continue;
    }
    if (group.code === 100) {
      inSubclass = group.value.trim() === TABLESTYLE_SUBCLASS;
      continue;
    }
    if (group.code === 3 && handleMatches && inSubclass) {
      const name = group.value.trim();
      return name === "" ? undefined : name;
    }
  }
  return undefined;
}

/**
 * The ROOT DICTIONARY key the `TABLESTYLE` records are filed under. ezdxf
 * registers the class with `dict_name="ACAD_TABLESTYLE"`
 * (`ezdxf/entities/acad_table.py`), and AutoCAD's own OBJECTS section names the
 * same child on the root dictionary.
 */
export const TABLESTYLE_DICTIONARY_KEY = "ACAD_TABLESTYLE";

/**
 * The `TABLESTYLE` behind an `ACAD_TABLE`'s group 342, packaged as a record an
 * export can CARRY into a new drawing (`dxfBridge.carriedObjectRecords.ts`).
 *
 * Group 342 is in the DXF HARD-POINTER range, and a wblock clone "clones hard
 * owners and hard pointers" (Autodesk, ObjectARX Developer's Guide — *Deep Clone
 * Basics*), so a WBLOCKed table must take its style with it or the pointer lands
 * on nothing in the written file. This is the source-side half of that: it reads
 * the style NAME through the ONE parser that already owns the name↔handle
 * resolution ({@link resolveTableStyleName} — never a second, looser scan) and
 * pairs it with the record's verbatim groups.
 *
 * `undefined` when the handle names no `TABLESTYLE` in this stream, or one with
 * no name — nothing is fabricated, and a table whose style is already missing
 * upstream stays exactly as broken as it was rather than gaining an invented
 * "Standard" (CLAUDE.md ПРИОРИТЕТ #1).
 */
export function carriedTableStyleRecord(
  rawObjectGroups: readonly RawGroup[] | undefined,
  styleHandle: string | undefined,
): CarriedObjectRecord | undefined {
  const entryName = resolveTableStyleName(rawObjectGroups, styleHandle);
  if (entryName === undefined || styleHandle === undefined) return undefined;
  const groups = sliceRawObjectRecord(rawObjectGroups, styleHandle);
  if (groups === undefined) return undefined;
  return {
    dictionaryKey: TABLESTYLE_DICTIONARY_KEY,
    entryName,
    // VERBATIM, not trimmed: this value is what the merge keys its reference
    // remap on, and `remapEntityHandleReferences` probes that map with the
    // entity's stored handle exactly as the entity stores it (lowercased, never
    // trimmed). A normalised key here would simply miss a 342 that carries the
    // source file's padding, and the pointer would be left dangling — the very
    // defect this carries the record to fix.
    sourceHandle: styleHandle,
    groups,
  };
}
